import { Component, lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { protectedRouteDecision, signInPath } from '@/lib/navigation-usability';
import '@/lib/dev-scenarios'; // Import for dev console access

// Lazily-loaded pages — each becomes its own JS chunk for faster initial load
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const SendFilesPage = lazy(() => import('@/pages/SendFilesPage').then(m => ({ default: m.SendFilesPage })));
const UploadPage = lazy(() => import('@/pages/UploadPage').then(m => ({ default: m.UploadPage })));
const MyFilesPage = lazy(() => import('@/pages/MyFilesPage').then(m => ({ default: m.MyFilesPage })));
const ActivityPage = lazy(() => import('@/pages/ActivityPage').then(m => ({ default: m.ActivityPage })));
const DownloadPage = lazy(() => import('@/pages/DownloadPage').then(m => ({ default: m.DownloadPage })));
const HelpPage = lazy(() => import('@/pages/HelpPage').then(m => ({ default: m.HelpPage })));
const ExternalCollectionEntryPage = lazy(() => import('@/pages/ExternalCollectionEntryPage'));
const UploadToCollectionPage = lazy(() => import('@/pages/UploadToCollectionPage'));
const StaffReceiveFilesPage = lazy(() => import('@/pages/StaffReceiveFilesPage'));
const CollectionDetailPage = lazy(() => import('@/pages/CollectionDetailPage'));
const CreateCollectionPage = lazy(() => import('@/pages/CreateCollectionPage'));
const ExternalAccessRequestPage = lazy(() => import('@/pages/ExternalAccessRequestPage'));
const SignInPage = lazy(() => import('@/pages/SignInPage'));
const SignInVerifyPage = lazy(() => import('@/pages/SignInVerifyPage'));
const SponsorApprovalsPage = lazy(() => import('@/pages/SponsorApprovalsPage'));

// Admin pages — loaded only when an admin visits /admin/*
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminFileSearchPage = lazy(() => import('@/pages/admin/AdminFileSearchPage').then(m => ({ default: m.AdminFileSearchPage })));
const AdminBlocklistPage = lazy(() => import('@/pages/admin/AdminBlocklistPage').then(m => ({ default: m.AdminBlocklistPage })));
const AdminAuditPage = lazy(() => import('@/pages/admin/AdminAuditPage').then(m => ({ default: m.AdminAuditPage })));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const AdminFederatedTenantsPage = lazy(() => import('@/pages/admin/AdminFederatedTenantsPage').then(m => ({ default: m.AdminFederatedTenantsPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminSyncPage = lazy(() => import('@/pages/admin/AdminSyncPage').then(m => ({ default: m.AdminSyncPage })));
const AdminFailedEmailsPage = lazy(() => import('@/pages/admin/AdminFailedEmailsPage').then(m => ({ default: m.AdminFailedEmailsPage })));

function PageLoader() {
  return (
    <div role="status" aria-live="polite" className="flex items-center justify-center gap-3 min-h-48">
      <div aria-hidden="true" className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <span>Loading page...</span>
    </div>
  );
}

class RouteErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(previousProps: { resetKey: string }) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <main className="max-w-xl mx-auto p-6 space-y-4">
        <div role="alert">
          <h1 className="text-2xl font-bold">Unable to load this page</h1>
          <p className="text-muted-foreground mt-2">Your page could not be displayed. Please try again.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => window.location.reload()}>Retry</Button>
          <Button variant="outline" asChild><Link to="/">Home</Link></Button>
        </div>
      </main>
    );
  }
}

function ProtectedRoute({ children, requireAdmin = false }: { children: ReactNode; requireAdmin?: boolean }) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  const decision = protectedRouteDecision(isAuthenticated, isAdmin, requireAdmin);
  if (decision === 'signin') {
    return <Navigate to={signInPath(location.pathname, location.search, location.hash)} replace />;
  }

  if (decision === 'denied') {
    return (
      <section className="max-w-xl mx-auto space-y-4">
        <div role="alert">
          <h1 className="text-2xl font-bold">Administrator access required</h1>
          <p className="text-muted-foreground mt-2">You are signed in, but your account does not have permission to view this page.</p>
        </div>
        <Button variant="outline" asChild><Link to="/">Home</Link></Button>
      </section>
    );
  }

  return <>{children}</>;
}

export default function App() {
  const location = useLocation();

  return (
    <RouteErrorBoundary resetKey={location.key}>
    <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Public download route - no layout */}
      <Route path="/d/:token" element={<DownloadPage />} />
      
      {/* Main app with layout */}
      <Route element={<Layout />}>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/send" element={<SendFilesPage />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/external/enter-collection" element={<ExternalCollectionEntryPage />} />
        <Route path="/external/upload/:collectionId" element={<UploadToCollectionPage />} />
        <Route path="/external/upload" element={<UploadToCollectionPage />} />
        <Route path="/request-access" element={<ExternalAccessRequestPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signin/verify" element={<SignInVerifyPage />} />
        <Route path="/help" element={<HelpPage />} />

        {/* Protected user routes */}
        <Route
          path="/files"
          element={
            <ProtectedRoute>
              <MyFilesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/approvals"
          element={
            <ProtectedRoute>
              <SponsorApprovalsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/receive"
          element={
            <ProtectedRoute>
              <StaffReceiveFilesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collections/new"
          element={
            <ProtectedRoute>
              <CreateCollectionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collections/:collectionId"
          element={
            <ProtectedRoute>
              <CollectionDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/files"
          element={
            <ProtectedRoute requireAdmin>
              <AdminFileSearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blocklist"
          element={
            <ProtectedRoute requireAdmin>
              <AdminBlocklistPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute requireAdmin>
              <AdminAuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/identity-providers"
          element={
            <ProtectedRoute requireAdmin>
              <AdminFederatedTenantsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireAdmin>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sync"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSyncPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/failed-emails"
          element={
            <ProtectedRoute requireAdmin>
              <AdminFailedEmailsPage />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={
          <section className="max-w-xl mx-auto space-y-4">
            <h1 className="text-2xl font-bold">Page not found</h1>
            <p className="text-muted-foreground">This address does not match an available page.</p>
            <Button variant="outline" asChild><Link to="/">Home</Link></Button>
          </section>
        } />
      </Route>
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
}
